import { useState, useSyncExternalStore } from 'react';
import type { BoardSnapshot, CardSummary, CustomField } from '@kaba/shared';
import { FieldInput } from '../components/card/Fields';
import { Button, Sheet } from '../components/ui/ui';
import { toast } from './toast';

/**
 * 0.81: „Beim Hineinziehen fragen nach …“ – vor dem Verschieben die Felder der Zielspalte abfragen.
 * Abbrechen lässt die Karte, wo sie war; „Verschieben“ speichert die Werte und verschiebt dann.
 */
type Pending = {
  card: CardSummary;
  column: { title: string };
  fields: CustomField[];
  required: string[];
  members: BoardSnapshot['members'];
  done: (values: Record<string, unknown>) => Promise<void>;
};
let pending: Pending | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function askEntryFields(p: Pending) {
  pending = p;
  emit();
}

export function EntryAskHost() {
  const p = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => pending,
  );
  return p ? <EntryAskDialog key={p.card.id + p.column.title} p={p} /> : null;
}

function EntryAskDialog({ p }: { p: Pending }) {
  const [values, setValues] = useState<Record<string, unknown>>(() => Object.fromEntries(p.fields.map((f) => [f.id, p.card.custom[f.id] ?? null])));
  const [busy, setBusy] = useState(false);
  const close = () => {
    pending = null;
    emit();
  };
  const empty = (v: unknown) => v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length);
  const missing = p.fields.filter((f) => p.required.includes(f.id) && empty(values[f.id]));
  return (
    <Sheet open onOpenChange={(o) => !o && close()} title={`Nach „${p.column.title}“ verschieben`} side="center">
      <form
        className="flex flex-col gap-3 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          // Das zuletzt bearbeitete Feld übernimmt seinen Wert beim Verlassen – darum Fokus erst lösen
          (document.activeElement as HTMLElement | null)?.blur?.();
          setTimeout(() => {
            if (missing.length) return toast.error(`Bitte noch ausfüllen: ${missing.map((f) => f.name).join(', ')}`);
            setBusy(true);
            void p.done(values).finally(() => {
              setBusy(false);
              close();
            });
          }, 0);
        }}
      >
        <p className="truncate text-sm text-[var(--ink-soft)]">„{p.card.title}“</p>
        {p.fields.map((f) => (
          <label key={f.id} className="flex flex-col gap-1">
            <span className="text-sm font-medium">
              {f.name}
              {p.required.includes(f.id) && <span className="text-rose-600"> *</span>}
            </span>
            <FieldInput field={f} value={values[f.id] ?? null} canEdit members={p.members} onChange={(v) => setValues((x) => ({ ...x, [f.id]: v }))} />
          </label>
        ))}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={close}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            Verschieben
          </Button>
        </div>
      </form>
    </Sheet>
  );
}
